import React, { useState, useEffect, useRef, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import tableStyles from "../../css/workingsTable.module.css";
import cfStyles from "../../css/cashFlowTable.module.css";
import SidePanel from "./SidePanel.jsx";
import { ReactComponent as LinkIcon } from "../../assets/icons/Link.svg";
import { ReactComponent as Add } from "../../assets/icons/AddPlus.svg";
import {
  setCashflowWorkingsRowMappings,
  getCashflowDropdowns,
  getConsolCashflowDropdowns,
  getPopulateFSDetailsApiHandler,
  getConsolPopulateFSDetailsApiHandler,
  getTemplateDetailsApiHandler,
  updateCashflowWorkings,
  updatePopulateFSApiHandler,
  getCashflowRowOptions,
  updateSubCashflowWorkingsApiHandler,
  deleteCashflowWorkingsRowApiHandler,
  deleteSubCashflowWorkings,
  createCashflowWorkings,
  createSubCashflow,
  createSubCashflowWorkings,
  replaceNotesTableApiHandler,
  replaceConsolCashflowTableApiHandler,
} from "../../../WorkSpace/Components/ApiServices/apiHandler";
import swal from "sweetalert";
import linkedIcon from "../../assets/icons/linked.png";
import DeletePopup from "../../elements/DeletePopup";
import SuccessPopup from "../../elements/SuccessPopup";
import  FormulaIcon from "../../assets/icons/formulaIcon.png";
import { ReactComponent as DeleteRedIcon } from "../../assets/icons/delete_red.svg";
import BoldIcon from "../../../myfin_v2/assets/icons/BoldIcon.png";
import { ReactComponent as DownArrow } from "../../assets/icons/down-arrow.svg";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import menuIcon from "../../assets/icons/Menu.png";
// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const formatIndianNumber = (val) => {
  if (val == null || val === "") return "";
  
  let str = String(val).replace(/,/g, "").trim();

  if (str.startsWith("(") && str.endsWith(")")) {
    str = "-" + str.slice(1, -1).trim();
  }
  
  const num = Number(str);
  if (isNaN(num)) return String(val);

  if (num < 0) {
    return "(" + Math.abs(num).toLocaleString("en-IN") + ")";
  }
  return num.toLocaleString("en-IN");
};

const toNumberOrNull = (val) => {
  if (val === "" || val === null || val === undefined) return null;
  
  let str = String(val).replace(/,/g, "").trim();
  
  // Handle bracketed negative numbers
  if (str.startsWith("(") && str.endsWith(")")) {
    str = "-" + str.slice(1, -1).trim();
  }
  
  const num = Number(str);
  return isNaN(num) ? null : num;
};

const createDefaultSubNoteData = (rowName, adj, item) => [
  {
    dataType: "table",
    tableTitle: rowName || "Sub-working movement",
    table: [
       { "0": adj || "", "1": item || "", "2": "0" } 
    ],
    tableRow: [
      { name: rowName || "row1", lineType: "line item" }
    ],
    tableColumn: [
      { name: "Adjustment name", lineType: "line item" }
    ]
  }
];

const sanitizeTemplateRow = (row) => {
  const lineType = row?.lineType || "line item";
  const sanitized = {
    name: row?.name || row?.particular || "",
    lineType,
  };
  if (typeof row?.isBold === "boolean") sanitized.isBold = row.isBold;
  if (["total", "subtotal"].includes(row?.formulaType)) {
    sanitized.formulaType = row.formulaType;
  }
  if (lineType === "formula") {
    sanitized.formula = Array.isArray(row?.formula) ? row.formula : [];
  }
  return sanitized;
};

const sanitizeTemplateColumn = (column) => {
  const lineType = column?.lineType || "line item";
  const sanitized = { name: column?.name || "", lineType };
  if (lineType === "formula") {
    sanitized.formula = Array.isArray(column?.formula) ? column.formula : [];
  }
  return sanitized;
};

const sanitizeTemplateTableFormulaFields = (table) => ({
  ...Object.fromEntries(
    Object.entries(table || {}).filter(([key]) => key !== "_id" && key !== "subNotes")
  ),
  ...(Array.isArray(table?.tableRow) && {
    tableRow: table.tableRow.map(sanitizeTemplateRow),
  }),
  ...(Array.isArray(table?.tableColumn) && {
    tableColumn: table.tableColumn.map(sanitizeTemplateColumn),
  }),
});

const sanitizeSubNoteData = (data) =>
  (Array.isArray(data) ? data : []).map((block) => {
    if (block?.dataType !== "table") return block;
    return {
      ...block,
      ...(Array.isArray(block.tableRow) && {
        tableRow: block.tableRow.map(sanitizeTemplateRow),
      }),
      ...(Array.isArray(block.tableColumn) && {
        tableColumn: block.tableColumn.map(sanitizeTemplateColumn),
      }),
    };
  });


const buildCleanTemplateTable = (
  tbl,
  updatedRowId,
  updatedFields,
  currentRowsMap,
  updatedRowIndex,
  originalWorkingRowName,
  rowMappingsOverride
) => {
  const tableFields = Object.fromEntries(
    Object.entries(tbl || {}).filter(([key]) => key !== "_id" && key !== "subNotes")
  );

  // Look up the original row by id first (index is wrong after drag/reorder)
  const originalRow = updatedRowId
    ? (tbl.tableRow || []).find(
        (tr) => String(tr._id || tr.id || tr.rowID) === String(updatedRowId)
      )
    : updatedRowIndex !== undefined
    ? tbl.tableRow?.[updatedRowIndex]
    : undefined;
  const originalRowName = originalRow?.name || originalRow?.particular || "";

  const result = {
    ...tableFields,
    dataType: tbl.dataType || "table",
    tableTitle: tbl.tableTitle || "",
    tableType: tbl.tableType || "content",
    table: Array.isArray(tbl.table) ? tbl.table : [],
    tableRow: (tbl.tableRow || []).map((tr, rowIndex) => {
      let rowName = tr.name || tr.particular || "";
      let isBold = tr.isBold || false;
      let lineType = tr.lineType || "line item";

      const isUpdatedRow = updatedRowId
        ? (tr._id === updatedRowId || tr.id === updatedRowId || tr.rowID === updatedRowId)
        : updatedRowIndex !== undefined && rowIndex === updatedRowIndex;

      if (isUpdatedRow) {
        if (updatedFields?.name !== undefined) rowName = updatedFields.name;
        if (updatedFields?.isBold !== undefined) isBold = updatedFields.isBold;
      } else if (currentRowsMap && currentRowsMap[tr._id]) {
        const rowData = currentRowsMap[tr._id];
        if (rowData.name !== undefined) rowName = rowData.name;
        if (rowData.isBold !== undefined) isBold = rowData.isBold;
      }

      const cleanRow = {
        name: rowName,
        lineType: lineType,
        isBold: isBold,
      };

      if (lineType === "formula") {
        cleanRow.formula = Array.isArray(tr.formula) ? tr.formula : [];
      }
      if (["total", "subtotal"].includes(tr.formulaType)) {
        cleanRow.formulaType = tr.formulaType;
      }

      return cleanRow;
    }),
    tableColumn: (tbl.tableColumn || []).map((tc) => {
      const lineType = tc.lineType || "line item";
      const column = {
        name: tc.name || "",
        lineType,
      };
      if (lineType === "formula") {
        column.formula = Array.isArray(tc.formula) ? tc.formula : [];
      }
      return column;
    }),
  };

  if (Array.isArray(tbl.cellFormulas) && tbl.cellFormulas.length > 0) {
    result.cellFormulas = tbl.cellFormulas.map((formula) =>
      updatedFields?.name !== undefined &&
      originalRowName &&
      normalize(formula?.row) === normalize(originalRowName)
        ? { ...formula, row: updatedFields.name }
        : formula
    );
  }

  // rowMappings: prefer the live UI state when provided
  if (Array.isArray(rowMappingsOverride)) {
    result.rowMappings = rowMappingsOverride;
  } else if (Array.isArray(tbl.rowMappings) && tbl.rowMappings.length > 0) {
    result.rowMappings = tbl.rowMappings;
  }

  return result;
};

const FIELDS = [
  ["name",     "Enter text",    "input",  false],
  ["cy",       "0",             "input",  true ],
  ["py",       "0",             "input",  true ],
  ["adj",      "Selection",     "select", false],
  ["item",     "Selection",     "select", false],
  ["amountCY", "0",             "input",  false],
  ["amountPY", "0",             "input",  false],
  ["remarks",  "Enter remarks", "input",  false],
];

const normalize = (val) =>
  String(val || "").trim().toLowerCase();

const cleanAlphaNum = (val) =>
  String(val || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const findRowMapping = (rowMappings = [], rowName = "") => {
  if (!Array.isArray(rowMappings) || !rowMappings.length || !rowName) return undefined;

  const targetNorm = normalize(rowName);
  const targetClean = cleanAlphaNum(rowName);

  // 1. Exact normalized match
  let match = rowMappings.find(
    (m) => normalize(m?.workingsRow) === targetNorm
  );
  if (match) return match;

  // 2. Alphanumeric match (ignores spaces, colons, punctuation)
  match = rowMappings.find(
    (m) => cleanAlphaNum(m?.workingsRow) === targetClean
  );
  if (match) return match;

  return undefined;
};

// Builds rowMappings from the live UI rows (always the latest adj / item)
const buildRowMappingsFromRows = (rows = []) =>
  (rows || [])
    .filter((r) => r.type === "data" && r.data?.name && r.data?.adj && r.data?.item)
    .map((r) => ({
      workingsRow: r.data.name,
      cashflowSection: r.data.adj,
      cashflowRow: r.data.itemOriginal || r.data.item,
    }));

const findSectionOption = (options = [], sectionName = "") => {
  if (!Array.isArray(options) || !sectionName) return undefined;
  const targetNorm = normalize(sectionName);
  const targetClean = cleanAlphaNum(sectionName);

  let found = options.find((s) => {
    const sName = typeof s === "string" ? s : s?.section?.name || s?.section || "";
    return normalize(sName) === targetNorm;
  });
  if (found) return found;

  found = options.find((s) => {
    const sName = typeof s === "string" ? s : s?.section?.name || s?.section || "";
    return cleanAlphaNum(sName) === targetClean;
  });
  if (found) return found;

  found = options.find((s) => {
    const sName = typeof s === "string" ? s : s?.section?.name || s?.section || "";
    const sClean = cleanAlphaNum(sName);
    if (!sClean || !targetClean) return false;
    return sClean.includes(targetClean) || targetClean.includes(sClean);
  });
  return found;
};

const findSectionByRowId = (options = [], rowIdOrVal = "") => {
  if (!Array.isArray(options) || !rowIdOrVal) return undefined;
  const targetId = String(rowIdOrVal).trim();
  const targetClean = cleanAlphaNum(rowIdOrVal);

  for (const s of options) {
    const sName = typeof s === "string" ? s : s?.section?.name || s?.section || "";
    for (const r of s.rows || []) {
      const rId = typeof r === "string" ? r : (r?._id || r?.rowID || r?.val || r?.row || "");
      const rLabel = typeof r === "string" ? r : (r?.row || r?.label || "");

      if (
        (rId && String(rId) === targetId) ||
        (rLabel && String(rLabel) === targetId) ||
        (targetClean && rId && cleanAlphaNum(rId) === targetClean) ||
        (targetClean && rLabel && cleanAlphaNum(rLabel) === targetClean)
      ) {
        return { sectionName: sName, rowLabel: rLabel || rId, rowId: rId };
      }
    }
  }
  return undefined;
};


// ─────────────────────────────────────────────────────────────────────────────
// Injected CSS
// ─────────────────────────────────────────────────────────────────────────────

const INJECTED_CSS = `
  .cf-tight-wrap {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
  }
  .cf-tight-wrap input {
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: #1a202c;
    padding: 2px 0;
    min-width: 40px;
    border-radius: 4px;
    transition: border 0.15s ease, box-shadow 0.15s ease;
  }
  .cf-tight-wrap input:focus {
    border: 1px solid #c2e6d1 !important;
    outline: none;
    border-radius: 4px;
  }
  .cf-name-wrapper input:focus,
  .cf-name-wrapper textarea:focus {
    border: 1px solid #c2e6d1 !important;
    border-radius: 4px;
    outline: none;
  }
  .cf-tight-wrap .cf-link-always {
    opacity: 1 !important;
  }
  .cf-dd-wrap {
    position: relative;
    width: 90%;
  }
  .cf-dd-trigger {
    width: 100%;
    padding: 8px 10px;
    font-size: 13px;
    border: 1px solid #c2e6d1;
    border-radius: 8px;
    background: white;
    color: #205A46;
    cursor: pointer;
    text-align: left;
    appearance: none;
    -webkit-appearance: none;
    left: 0;
    right: 0;
    z-index: 9999;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .cf-dd-list {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 9999;
    background: #ffffff;
    border: 0.5px solid #c2e6d1;
    border-radius: 10px;
    overflow-y: auto;
    max-height: 175px;
    display: none;
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    font-weight: 400 !important;
  }
  .cf-dd-list.open {
    display: block;
  }
  .cf-dd-item {
    padding: 7px 10px;
    font-size: 13px;
    cursor: pointer;
    color: #205A46;
    white-space: normal;
    word-break: break-word;
    background: white;
    font-weight: 400 !important;
  }
  .cf-dd-item:hover {
    background: #f8f9f9;
  }
  .cf-dd-item.cf-highlighted {
    background: #f8f9f9 !important;
  }
  .cf-dd-item.cf-active {
    background: #f4faf5;
    font-weight: 600 !important;
  }
  .cf-dd-item + .cf-dd-item {
    border-top: 0.5px solid #e2e8e5;
  }
  .cf-search-input {
    width: 100%;
    padding: 8px 10px;
    font-size: 13px !important;
    border: 1px solid #c2e6d1!important;
    border-radius: 8px!important;
    color: #205A46;
    outline: none !important;
    box-shadow: none !important;
    box-sizing: border-box;
  }
  .cf-search-input:focus,
  .cf-search-input:focus-visible,
  .cf-search-input:active {
    border: 1px solid #c2e6d1 !important;
    border-radius: 8px !important;
    outline: none !important;
    box-shadow: none !important;
  }
  .cf-bold-input {
    font-weight: bold !important;
  }
  .cf-tooltip {
    position: relative;
  }
  .cf-tooltip:hover::after {
    content: attr(data-tooltip);
    position: fixed;
    background: #000000;
    color: #ffffff;
    font-size: 12px;
    font-family: 'DM Sans', sans-serif;
    font-weight: 400;
    padding: 5px 8px;
    border-radius: 4px;
    white-space: normal;
    z-index: 99999;
    pointer-events: none;
    max-width: 260px;
    line-height: 1.4;
    transform: translateY(-110%);
  }
  .cf-no-results {
    padding: 8px 10px;
    font-size: 13px;
    color: #888;
    background: #f8f9f9;
    font-weight: 400 !important;
  }
  .cf-name-wrapper {
    position: relative;
    width: 95%;
  }
`;

if (
  typeof document !== "undefined" &&
  !document.getElementById("cf-workings-injected")
) {
  const tag = document.createElement("style");
  tag.id = "cf-workings-injected";
  tag.textContent = INJECTED_CSS;
  document.head.appendChild(tag);
}

// ────────────────────────────────────────────────────────────────────────────
// AdjDropdown — section selector
// ─────────────────────────────────────────────────────────────────────────────

const AdjDropdown = ({ value, onChange, options, isEditable, isBold }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const [isSearching, setIsSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) return;
    const match = options.find(
      (opt) =>
        normalize(opt) === normalize(value) ||
        cleanAlphaNum(opt) === cleanAlphaNum(value) ||
        (cleanAlphaNum(value).length >= 4 &&
          (cleanAlphaNum(opt).includes(cleanAlphaNum(value)) ||
            cleanAlphaNum(value).includes(cleanAlphaNum(opt))))
    );
    setQuery(match || value || "");
  }, [value, open, options]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setFocused(false);
        if (inputRef.current) inputRef.current.blur();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const activeEl = listRef.current.children[activeIndex];
      if (activeEl) {
        activeEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeIndex]);

  const filtered = !isSearching
    ? options 
    : options.filter(opt => 
        String(opt).toLowerCase().includes(query.toLowerCase())
      );

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter") {
        setOpen(true);
        setIsSearching(false);
        setActiveIndex(-1);
        e.preventDefault();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => {
        const next = prev + 1;
        return next >= filtered.length ? 0 : next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => {
        const next = prev - 1;
        return next < 0 ? filtered.length - 1 : next;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < filtered.length) {
        const selectedOpt = filtered[activeIndex];
        onChange(selectedOpt);
        setQuery(selectedOpt);
        setIsSearching(false);
        setOpen(false);
        setFocused(false);
        if (inputRef.current) inputRef.current.blur();
      } else if (query) {
        onChange(query);
        setIsSearching(false);
        setOpen(false);
        setFocused(false);
        if (inputRef.current) inputRef.current.blur();
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="cf-dd-wrap" ref={ref}>
      <div 
        style={{ position: "relative" }}
        onClick={() => {
          if (isEditable) {
            setOpen(true);
            setIsSearching(false);
            setActiveIndex(-1);
          }
        }}
      >
        <input
          ref={inputRef}
          type="text"
          className={`cf-search-input ${isBold ? "cf-bold-input" : ""}`.trim()}
          style={{ 
            width: "100%", 
            padding: "8px 10px",
            paddingRight: "30px", 
            textOverflow: "ellipsis",
            border: focused ? "1px solid #1D9E75" : "1px solid #c2e6d1",
            borderRadius: "8px",
            background: "#ffffff",
            outline: "none",
            fontSize: "13px",
            fontWeight: isBold ? "bold" : 400,
            color: "#205A46",
            boxSizing: "border-box",
          }}
          placeholder="Selection"
          value={query}
          onFocus={() => {
            setFocused(true);
            setOpen(true);
            setIsSearching(false);
            setActiveIndex(-1);
          }}
          onBlur={() => setFocused(false)}
          onKeyDown={handleKeyDown}
          onChange={(e) => {
            const val = e.target.value;
            setQuery(val);
            setIsSearching(true);
            onChange(val);
            setOpen(true);
            setActiveIndex(-1);
          }}
          readOnly={!isEditable}
        />
        <DownArrow 
          width={14} 
          height={14} 
          style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", cursor: "pointer", pointerEvents: "none" }} 
        />
      </div>
      {open && isEditable && (
        <div 
          className="cf-dd-list open" 
          ref={listRef}
          style={{ maxHeight: "200px", overflowY: "auto" }}
        >
          {filtered.length > 0 ? (
            filtered.map((opt, i) => (
              <div
                key={i}
                className={`cf-dd-item${value === opt ? " cf-active" : ""}${activeIndex === i ? " cf-highlighted" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(opt);
                  setQuery(opt);
                  setIsSearching(false);
                  setOpen(false);
                  setFocused(false);
                  if (inputRef.current) inputRef.current.blur();
                }}
              >
                {opt}
              </div>
            ))
          ) : (
            <div className="cf-dd-item no-res">No results</div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ItemSearchDropdown — row selector with search
// ─────────────────────────────────────────────────────────────────────────────

const ItemSearchDropdown = ({ value, displayValue, onChange, options, isEditable, isBold }) => {
  const [query, setQuery] = React.useState(displayValue || value || "");
  const [open, setOpen] = React.useState(false);
  const [isSearching, setIsSearching] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [focused, setFocused] = React.useState(false);
  const ref = React.useRef(null);
  const listRef = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (open) return;

    const match = options.find(
      (o) =>
        String(o.val) === String(value) ||
        String(o.label).toLowerCase() === String(value).toLowerCase() ||
        cleanAlphaNum(o.label) === cleanAlphaNum(value) ||
        cleanAlphaNum(o.val) === cleanAlphaNum(value) ||
        (cleanAlphaNum(value).length >= 4 &&
          (cleanAlphaNum(o.label).includes(cleanAlphaNum(value)) ||
            cleanAlphaNum(value).includes(cleanAlphaNum(o.label))))
    );

    if (match) {
      setQuery(match.label);
    } else if (displayValue || value) {
      setQuery(displayValue || value); 
    } else {
      setQuery("");
    }
  }, [value, displayValue, options, open]);

  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setFocused(false);
        if (inputRef.current) inputRef.current.blur();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  React.useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const activeEl = listRef.current.children[activeIndex];
      if (activeEl) {
        activeEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeIndex]);

  const filtered = !isSearching
    ? options
    : options.filter((o) =>
        String(o.label || "")
          .toLowerCase()
          .includes(String(query || "").toLowerCase())
      );

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter") {
        setOpen(true);
        setIsSearching(false);
        setActiveIndex(-1);
        e.preventDefault();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => {
        const next = prev + 1;
        return next >= filtered.length ? 0 : next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => {
        const next = prev - 1;
        return next < 0 ? filtered.length - 1 : next;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < filtered.length) {
        const selectedOpt = filtered[activeIndex];
        setQuery(selectedOpt.label);
        setIsSearching(false);
        onChange({
          value: selectedOpt.val,
          original: selectedOpt.label
        });
        setOpen(false);
        setFocused(false);
        if (inputRef.current) inputRef.current.blur();
      } else if (query) {
        onChange({
          value: query,
          original: query
        });
        setIsSearching(false);
        setOpen(false);
        setFocused(false);
        if (inputRef.current) inputRef.current.blur();
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="cf-dd-wrap" ref={ref}>
      <div style={{ position: "relative" }}>
        <input
          ref={inputRef}
          type="text"
          className={`cf-search-input cf-tooltip ${isBold ? "cf-bold-input" : ""}`.trim()}
          data-tooltip={query || ""}
          placeholder="Type to search..."
          value={query}
          onKeyDown={handleKeyDown}
          onChange={(e) => {
            const val = e.target.value;
            setQuery(val);
            setOpen(true);
            setIsSearching(true);
            setActiveIndex(-1);

            onChange({
              value: val,
              original: val
            });
          }}
          onFocus={() => {
            setFocused(true);
            setOpen(true);
            setIsSearching(false);
            setActiveIndex(-1);
          }}
          onBlur={() => setFocused(false)}
          onClick={() => {
            setOpen(true);
            setIsSearching(false);
            setActiveIndex(-1);
          }}
          style={{ 
            width: "100%", 
            padding: "8px 10px",
            paddingRight: "30px", 
            textOverflow: "ellipsis",
            border: focused ? "1px solid #1D9E75" : "1px solid #c2e6d1",
            borderRadius: "8px",
            background: "#ffffff",
            outline: "none",
            fontSize: "13px",
            fontWeight: isBold ? "bold" : 400,
            color: "#205A46",
            boxSizing: "border-box",
          }}  
        />
        <DownArrow
          width={14}
          height={14}
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
          }}
          onClick={() => {
            setOpen((o) => !o);
            setIsSearching(false);
          }}
        />
      </div>
      <div 
        className={`cf-dd-list${open ? " open" : ""}`}
        ref={listRef}
      >
        {filtered.length === 0 ? (
          <div className="cf-no-results">No results found</div>
        ) : (
          filtered.map((o, i) => (
            <div
              key={i}
              className={`cf-dd-item cf-tooltip${value === o.val ? " cf-active" : ""}${activeIndex === i ? " cf-highlighted" : ""}`}
              data-tooltip={o.label}
              onMouseDown={(e) => {
                e.preventDefault();
                setQuery(o.label);
                setIsSearching(false);
                onChange({
                  value: o.val,
                  original: o.label
                });
                setOpen(false);
                setFocused(false);
                if (inputRef.current) inputRef.current.blur();
              }}
            >
              {o.label}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// NumericInput — auto-sizing, Indian number formatting
// ─────────────────────────────────────────────────────────────────────────────

const NumericInput = ({ value, onChange, placeholder, disabled, style }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState("");

  const displayValue = editing ? draft : formatIndianNumber(value);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        type="text"
        placeholder={placeholder}
        value={displayValue}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => {
          setEditing(true);
          let v = (value || "").toString().replace(/,/g, "").trim();
          // Convert (1000) to -1000 for editing
          if (v.startsWith("(") && v.endsWith(")")) {
            v = "-" + v.slice(1, -1).trim();
          }
          setDraft(v);
        }}
        disabled={disabled}
        onBlur={() => {
          setEditing(false);

          const clean = draft.replace(/,/g, "");
          const parsed = clean === "" ? "" : Number(clean);

          onChange(parsed);
        }}
        style={{
          border: "none",
          outline: "none",
          background: "transparent",
          fontSize: "13px",
          color: "#111111",
          padding: "2px 0",
          width: "100%",
          textAlign: "right",
          paddingRight: "4px",
          ...style
        }}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CellInput — renders the correct input type per field
// ─────────────────────────────────────────────────────────────────────────────

const CellInput = ({
  field,
  fieldType,
  placeholder,
  value,
  onChange,
  onBlur,
  isTight,
  isLinked,
  onLinkClick,
  hideLinkIcon,
  rowOptions,
  rowData,
  isTemplate,
  isDisclosureTable = false,
  isEditable,
  isBold
}) => {
  const isDropdownWithOutsideIcon = field === "adj" || field === "item";
  const isReadOnly = field === "amountCY" || field === "amountPY";
  const isNumericField =
    field === "cy" || field === "py" || field === "amountCY" || field === "amountPY";

  const safeRowOptions = Array.isArray(rowOptions) ? rowOptions : [];
  

  if (isTight) {
    return (
      <div className={`${tableStyles.cell} cf-tight-wrap`}>
        <NumericInput
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={!isEditable}
          style={{ fontWeight: isBold ? "bold" : "inherit", paddingRight: "34px" }}
        />
        {(!hideLinkIcon || !isTemplate) && isTight && (isTemplate || field === "cy" || field === "py") && (
          <span
            className={`
              ${tableStyles.linkWrapper}
              ${isLinked ? tableStyles.linked : ""}
            `}
            onClick={onLinkClick}
            style={{
              background: isLinked ? "#205a46" : "#3AB67D",
              borderRadius: "50%",
              position: "absolute",
              right: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "28px",
              height: "28px",
              flexShrink: 0,
            }}
          >
            {isLinked ? (
              isLinked.type === "formula" ? (
                <img src={FormulaIcon} style={{ width: 18, height: 18 }} />
              ) : (
                <img src={linkedIcon} className={tableStyles.linkedIcon} />
              )
            ) : (
              <LinkIcon style={{ width: 16, height: 16, color: "#fff" }} />
            )}
          </span>
        )}
      </div>
    );
  }

  return (
    <div 
      className={field === "name" ? "cf-name-wrapper" : ""}
      style={{
        position: "relative",
        width: "95%",
        paddingLeft: field === "name" ? "0px" : "20px"
      }}
    >
  {fieldType === "select" ? (() => {
    const resolved = (!rowData?.adj && rowData?.item) ? findSectionByRowId(safeRowOptions, rowData.item) : null;
    const effectiveAdj = rowData?.adj || resolved?.sectionName || "";
    const effectiveDisplayValue = (rowData?.itemOriginal && !cleanAlphaNum(rowData.itemOriginal).startsWith("6a") && rowData.itemOriginal.length > 20)
      ? rowData.itemOriginal
      : (resolved?.rowLabel || rowData?.itemOriginal || rowData?.item);

    return field === "adj" ? (
      <AdjDropdown
        value={value || effectiveAdj}
        onChange={isEditable ? onChange : () => {}}
        options={safeRowOptions.map((s) =>
          typeof s.section === "string"
            ? s.section
            : s.section?.name || ""
        )}
        isEditable={isEditable}  
        isBold={isBold}
      />
    ) : (
      <ItemSearchDropdown
        value={value}
        displayValue={effectiveDisplayValue}
        onChange={isEditable ? onChange : () => {}}
        disabled={!isEditable}
        options={(() => {
          const selected = findSectionOption(safeRowOptions, effectiveAdj);
          return (selected?.rows || []).map((r) => {
            const label =
              typeof r === "string"
                ? r
                : r?.row ?? "";

            return {
              label: String(label),
              val: r?._id || r?.rowID || label || "",
            };
          });
        })()}
        isBold={isBold}
      />
    );
  })() : isNumericField ? (
    <NumericInput
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={!isEditable}
      style={{ fontWeight: isBold ? "bold" : "inherit" }}
    />
  ) : (
    <input
      disabled={!isEditable}
      readOnly={isReadOnly}
      style={{
        width: "100%",
        border: "none",
        outline: "none",
        background: "transparent",
        textAlign: "left",
        paddingRight: "28px",
        cursor: isEditable ? "text" : "not-allowed",
        fontWeight: isBold ? "bold" : "inherit",
      }}
      placeholder={placeholder}
      value={value ?? ""}
      onBlur={(event) => onBlur?.(event.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.target.blur();
      }}
      onChange={(e) => {
        if (!isEditable) return;
        onChange(e.target.value);
      }}
    />
  )}
</div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TableSection — renders rows for one table block
// ─────────────────────────────────────────────────────────────────────────────

const TableSection = ({
  title,
  rows,
  setRows,
  openPanel,
  linkedCells,
  hideLinkIcon,
  rowOptions,
  tableId,
  sectionId,
  section,
  isTemplate,
  groupingTemplateID,
  isDisclosureTable = false,
  isEditable,
  onRefresh,
  isStandalone,
  setSuccessMessage,
  setShowSuccessPopup,
  onRenameSaveStart,
  onRenameSaveEnd,
  isConsol = false
}) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const debounceRef = useRef({});
  const creatingRowIdsRef = useRef(new Set());
  const [rowToDelete, setRowToDelete] = useState(null);
  const [hoveredRowIdx, setHoveredRowIdx] = useState(null);
  const [draggedRowIdx, setDraggedRowIdx] = useState(null);

  const handleConfirmDelete = () => {
    if (!rowToDelete) return;
    const { rowId, rowIdx, isSubRow } = rowToDelete;
    setRowToDelete(null);

    const updated = safeRows.filter((_, i) => i !== rowIdx);

    if (isStandalone) {
      setRows(updated);
      syncStandaloneTable(updated, true)
        .then(() => {
          setSuccessMessage("Your line item has been deleted!");
          setShowSuccessPopup(true);
          if (typeof onRefresh === "function") onRefresh();
        })
        .catch((err) => {
          console.error("Delete error:", err);
          swal("Error", "Failed to delete line item", "error");
        });
      return;
    }

    const deletedRow = safeRows[rowIdx];
    const deletedRowName = rowToDelete?.name || deletedRow?.data?.name;

    const newSectionData = (section?.data || []).map((tbl) => {
      if (tbl._id !== tableId) return sanitizeTemplateTableFormulaFields(tbl);

      const filteredTableRows = (tbl.tableRow || []).filter((tr) => {
        const trId = String(tr._id || tr.id || tr.rowID || "");
        const trName = normalize(tr.name || tr.particular || "");
        if (rowId && trId && trId === String(rowId)) return false;
        if (deletedRowName && trName && trName === normalize(deletedRowName)) return false;
        return true;
      });

      const cleanTbl = buildCleanTemplateTable(
        { ...tbl, tableRow: filteredTableRows },
        undefined, undefined, undefined, undefined, undefined,
        buildRowMappingsFromRows(updated)
      );

      if (deletedRowName) {
        if (Array.isArray(cleanTbl.cellFormulas)) {
          cleanTbl.cellFormulas = cleanTbl.cellFormulas.filter(
            (f) => normalize(f?.row) !== normalize(deletedRowName)
          );
        }
        if (Array.isArray(cleanTbl.rowMappings)) {
          cleanTbl.rowMappings = cleanTbl.rowMappings.filter(
            (m) => normalize(m?.workingsRow) !== normalize(deletedRowName)
          );
        }
      }

      delete cleanTbl.subNotes;
      return cleanTbl;
    });

    const params = {
      groupingTemplateID,
      cashflowWorkingsID: sectionId,
    };

    const payload = {
      name: section?.name || "",
      groupingID: section?.groupingID || section?.groupingId || groupingTemplateID,
      data: newSectionData,
    };

    updateCashflowWorkings(params, payload)
      .then((res) => {
        if (res?.data?.success) {
          setRows(updated);

          setSuccessMessage("Your line item has been deleted!");
          setShowSuccessPopup(true);

          if (typeof onRefresh === "function") onRefresh();
        } else {
          throw new Error(res?.data?.error || "Failed to delete line item");
        }
      })
      .catch((err) => {
        console.error("Delete error:", err);
        swal(
          "Error",
          err?.response?.data?.error || err?.message || "Failed to delete line item",
          "error"
        );
      });
  };

  const createTemplateRowOnBlur = async (rowIdx, inputName) => {
    const currentRow = safeRows[rowIdx];
    const rowId = currentRow?.data?._id;
    const name = String(inputName ?? currentRow?.data?.name ?? "").trim();
    if (
      !isTemplate ||
      isConsol ||
      !currentRow?.data?.isNew ||
      !String(rowId || "").startsWith("temp_") ||
      !name ||
      creatingRowIdsRef.current.has(rowId)
    ) {
      return;
    }

    creatingRowIdsRef.current.add(rowId);
    const creatingRows = [...safeRows];
    creatingRows[rowIdx] = {
      ...currentRow,
      data: { ...currentRow.data, name, isNew: false, isCreating: true },
    };
    setRows(creatingRows);

    try {
      const newSectionData = (section?.data || []).map((tbl) => {
        if (tbl._id !== tableId) return sanitizeTemplateTableFormulaFields(tbl);

        const existingTableRows = Array.isArray(tbl.tableRow) ? [...tbl.tableRow] : [];
        const newRowObj = {
          name,
          lineType: "line item",
          isBold: currentRow?.data?.isBold || false,
        };
        const insertIndex = Math.min(Math.max(0, rowIdx - 1), existingTableRows.length);
        existingTableRows.splice(insertIndex, 0, newRowObj);

        const cleanTbl = buildCleanTemplateTable(
          { ...tbl, tableRow: existingTableRows },
          undefined, undefined, undefined, undefined, undefined,
          buildRowMappingsFromRows(creatingRows)
        );
        delete cleanTbl.subNotes;
        return cleanTbl;
      });

      const params = {
        groupingTemplateID,
        cashflowWorkingsID: sectionId,
      };

      const payload = {
        name: section?.name || "",
        groupingID: section?.groupingID || section?.groupingId || groupingTemplateID,
        data: newSectionData,
      };

      const response = await updateCashflowWorkings(params, payload);
      if (!response?.data?.success) {
        throw new Error(response?.data?.error || "Unable to create line item");
      }

      setSuccessMessage("Line item created successfully");
      setShowSuccessPopup(true);
      if (typeof onRefresh === "function") onRefresh();
    } catch (error) {
      const retryRows = [...creatingRows];
      retryRows[rowIdx] = {
        ...retryRows[rowIdx],
        data: { ...retryRows[rowIdx].data, isNew: true, isCreating: false },
      };
      setRows(retryRows);
      swal(
        "Error",
        error?.response?.data?.error || error?.message || "Unable to create line item",
        "error"
      );
    } finally {
      creatingRowIdsRef.current.delete(rowId);
    }
  };

  const toggleBold = (idx) => {
    const updated = [...safeRows];
    updated[idx].data.isBold = !updated[idx].data.isBold;
    setRows(updated);
    if (isStandalone) syncStandaloneTable(updated);
  };

  const syncStandaloneTable = async (rowsData, shouldRefresh = false) => {
    if (!isStandalone) return;

    const hasTempIds = (rowsData || []).some(
      (r) => r.type === "data" && String(r.data?._id || "").startsWith("temp_")
    );

    // Filter out static opening/closing helper blocks and map to flat DB schema objects
    const dbRows = (rowsData || [])
      .filter((r) => r.type === "data")
      .map((r) => {
        const rowObj = {
          particular: r.data.name || "",
          currentYear: r.data.cy ?? "",
          previousYear: r.data.py ?? "",
          cashflowSection: r.data.adj || "",
          cashflowRow: r.data.item || null,
          reflectedCY: r.data.amountCY ?? "",
          reflectedPY: r.data.amountPY ?? "",
          remarks: r.data.remarks || "",
          lineType: "line item",
          isBold: r.data.isBold || false,
        };
        if (r.data._id && !String(r.data._id).startsWith("temp_")) {
          rowObj._id = r.data._id;
        }
        return rowObj;
      });

    const params = {
      category: "cashflowWorkings",
      projectID: groupingTemplateID,
      groupID: sectionId,
      subgroupID: tableId,
    };

    const payload = {
      table: dbRows,
    };

    if (isConsol) {
      const consolParams = {
        consolID: groupingTemplateID,
        category: "cashflowWorkings",
        groupID: sectionId,
        subgroupID: tableId,
      };
      await replaceConsolCashflowTableApiHandler(consolParams, payload);
    } else {
      await replaceNotesTableApiHandler(params, payload);
    }

    // Immediately re-fetch this section's data after save on new rows, rename, rearrange, or delete
    if ((hasTempIds || shouldRefresh) && typeof onRefresh === "function") {
      onRefresh();
    }
  };
  
  const addNewRow = () => {
    const closingIndex = safeRows.findIndex((r) => r.type === "closing");
    const tempRowId = `temp_${Date.now()}`;

    const newRow = {
      type: "data",
      data: {
        _id: tempRowId,
        name: "",
        cy: "",
        py: "",
        adj: "",
        item: "",
        amountCY: "",
        amountPY: "",
        remarks: "",
        isNew: true,
        isCreating: false,
        isBold: false,
      },
    };

    const updated = [...safeRows];
    updated.splice(
      closingIndex === -1 ? updated.length : closingIndex,
      0,
      newRow
    );

    setRows(updated);
  };

  const handleDrag = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const updated = [...safeRows];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    setRows(updated);
    if (isStandalone) {
      syncStandaloneTable(updated, true)
        .then(() => {
          if (typeof onRefresh === "function") onRefresh();
        })
        .catch((err) => console.error("Standalone rearrange sync failed", err));
    } else if (isTemplate && section && !isConsol) {
      const originalStandardRowIds = safeRows
        .filter((row) => row.type === "data" && !row.data?.isSubRow)
        .map((row) => String(row.data?._id || ""));
      const orderedRowIds = updated
        .filter((row) => row.type === "data" && !row.data?.isSubRow)
        .map((row) => row.data?._id)
        .filter(Boolean);
      const standardOrderChanged =
        JSON.stringify(originalStandardRowIds) !== JSON.stringify(orderedRowIds.map(String));
      const originalSubRows = safeRows
        .filter((row) => row.type === "data" && row.data?.isSubRow);
      const originalSubRowIds = originalSubRows
        .map((row) => String(row.data?._id || ""));
      const orderedSubRows = updated.filter(
        (row) => row.type === "data" && row.data?.isSubRow
      );
      const subRowOrderChanged = JSON.stringify(originalSubRowIds) !== JSON.stringify(
        orderedSubRows.map((row) => String(row.data?._id || ""))
      );
      if (standardOrderChanged || subRowOrderChanged) {
        const requests = [];
        if (standardOrderChanged) {
          const newSectionData = (section.data || []).map((tbl) => {
            if (tbl._id !== tableId) return sanitizeTemplateTableFormulaFields(tbl);

            let tableRows = tbl.tableRow || [];
            const rowById = new Map(
              tableRows.map((row) => [String(row._id || row.id || row.rowID), row])
            );
            const orderedRows = orderedRowIds
              .map((id) => rowById.get(String(id)))
              .filter(Boolean);
            const includedIds = new Set(orderedRowIds.map(String));
            const remainingRows = tableRows.filter(
              (row) => !includedIds.has(String(row._id || row.id || row.rowID))
            );
            tableRows = [...orderedRows, ...remainingRows];

            return buildCleanTemplateTable(
              { ...tbl, tableRow: tableRows },
              undefined, undefined, undefined, undefined, undefined,
              buildRowMappingsFromRows(updated)
            );
          });

          requests.push(
            updateCashflowWorkings(
              { groupingTemplateID, cashflowWorkingsID: sectionId },
              {
                name: section.name || "",
                groupingID: section.groupingID || groupingTemplateID,
                data: newSectionData,
              }
            )
          );
        }

        if (subRowOrderChanged) {
          orderedSubRows.forEach((row, index) => {
            const targetSubNoteID = originalSubRows[index]?.data?._id;
            if (String(targetSubNoteID || "") === String(row.data?._id || "")) return;
            if (!targetSubNoteID || String(targetSubNoteID).startsWith("temp_")) return;

            const rawSubNote = row.data.raw || {};
            const payload = {
              name: row.data.name || rawSubNote.name || "",
              data: sanitizeSubNoteData(
                Array.isArray(rawSubNote.data) && rawSubNote.data.length > 0
                  ? rawSubNote.data
                  : createDefaultSubNoteData(row.data.name, row.data.adj, row.data.item)
              ).map((block) =>
                Object.fromEntries(
                  Object.entries(block || {}).filter(([key]) => key !== "_id" && key !== "id")
                )
              ),
            };

            requests.push(
              updateSubCashflowWorkingsApiHandler(
                {
                  groupingTemplateID,
                  cashflowWorkingsID: sectionId,
                  subNoteID: targetSubNoteID,
                },
                payload
              )
            );
          });
        }

        Promise.all(requests)
          .then(() => {
            if (requests.length && typeof onRefresh === "function") onRefresh();
          })
          .catch((err) => console.error("Cashflow workings row reorder failed", err));
      }
    }
  };

  return (
    <>
      {!!title && (
        <tr className={tableStyles.titleRow}>
          <td colSpan={FIELDS.length}>{title}</td>
        </tr>
      )}

      {safeRows.map((row, rowIdx) => {
        const rowName = (row.data?.name || "").toLowerCase();
        const isStaticRow = row.type === "staticOpening" || row.type === "staticClosing";
        const fontWeight = row.data?.isBold || isStaticRow ? "bold" : "normal";
          
        const isDraggable = !isStaticRow && isEditable && (isTemplate || isStandalone);

        if (!row.data) return null;

        return (
          <tr
            key={`data-${rowIdx}`}
            draggable={isDraggable}
            onMouseEnter={() => setHoveredRowIdx(rowIdx)}
            onMouseLeave={() => setHoveredRowIdx(null)}
            onDragStart={(e) => {
              if (!isDraggable) {
                e.preventDefault();
                return;
              }
              e.dataTransfer.setData("from", String(rowIdx));
              setTimeout(() => {
                setDraggedRowIdx(rowIdx);
              }, 0);
            }}
            onDragEnd={() => setDraggedRowIdx(null)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              if (!isDraggable) return;
              setDraggedRowIdx(null);
              const from = e.dataTransfer.getData("from");
              handleDrag(Number(from), rowIdx);
            }}
            style={{ 
              position: "relative",
              cursor: isDraggable ? "default" : "not-allowed",
              fontWeight: fontWeight,
              backgroundColor: draggedRowIdx === rowIdx ? "#c2e6d1" : undefined,
              opacity: draggedRowIdx === rowIdx ? 0.9 : 1
            }}
            className={isStaticRow ? tableStyles.openingClosingRow : `${tableStyles.dataRow} ${isDraggable ? "cf-data-row" : ""}`}
          >
            {FIELDS.map(([field, placeholder, fieldType, isTight], colIdx) => {
              const linkRowKey = row.data?._id || rowIdx;
              const cellKey = `${tableId}-${linkRowKey}-${colIdx}`;
              return (
                <td 
                  key={colIdx} 
                  className={tableStyles.cell}
                  style={{ 
                    position: field === "name" ? "relative" : "static",
                    textAlign: (field === "cy" || field === "py") ? "right" : "left",
                    borderTop: draggedRowIdx === rowIdx ? "1px solid #3AB67D" : undefined,
                    borderBottom: draggedRowIdx === rowIdx ? "1px solid #3AB67D" : undefined,
                    borderLeft: draggedRowIdx === rowIdx && colIdx === 0 ? "1px solid #3AB67D" : undefined,
                    borderRight: draggedRowIdx === rowIdx && colIdx === FIELDS.length - 1 ? "1px solid #3AB67D" : undefined,
                  }}
                >
                  {field === "name" && isDraggable && (
                    <div 
                      className={tableStyles.dragHandle} 
                      style={{ 
                        cursor: "grab",
                        position: "absolute",
                        left: "10px",
                        top: "50%",
                        transform: "translateY(-50%)"
                      }}
                      title="Drag to reorder"
                    >
                      <img src={menuIcon} alt="drag" style={{ cursor: "grab", width: 9 }} />
                    </div>
                  )}
                  <CellInput
                    hideLinkIcon={hideLinkIcon}
                    field={field}
                    fieldType={fieldType}
                    placeholder={placeholder}
                    isTemplate={isTemplate}
                    isDisclosureTable={isDisclosureTable}
                    isEditable={isEditable && !row.data?.isCreating}
                    value={row.data?.[field] ?? ""}
                    isBold={row.data?.isBold || isStaticRow}
                    onBlur={(inputValue) => {
                      if (field === "name") {
                        if (isTemplate) {
                          createTemplateRowOnBlur(rowIdx, inputValue);
                        } else if (isStandalone) {
                          const finalName = String(inputValue ?? safeRows[rowIdx]?.data?.name ?? "").trim();
                          const originalRow = safeRows[rowIdx]?.data?.raw;
                          const currentName = String(originalRow?.particular || originalRow?.name || "").trim();

                          if (finalName && finalName !== currentName) {
                            const updated = safeRows.map((r, i) => {
                              if (i !== rowIdx) return r;
                              return {
                                ...r,
                                data: {
                                  ...r.data,
                                  name: finalName,
                                },
                              };
                            });
                            setRows(updated);
                            onRenameSaveStart?.();
                            syncStandaloneTable(updated, true)
                              .catch((err) => console.error("Standalone Cashflow Workings rename sync failed", err))
                              .finally(() => onRenameSaveEnd?.());
                          }
                        }
                      }
                    }}
                    onChange={(val) => {
                      if (!isEditable) return;
                      const updated = safeRows.map((r, i) => {
                        if (i !== rowIdx) return r;
                        return {
                          ...r,
                          data: {
                            ...r.data,
                            [field]: typeof val === "object" ? val.value : val,
                            ...(field === "item" && typeof val === "object"
                              ? { itemOriginal: val.original }
                              : {}),
                            ...(field === "adj" ? { item: "" } : {}),
                          },
                        };
                      });
                      setRows(updated);

                      const currentRow = safeRows[rowIdx];
                      const isNewRow = currentRow?.data?.isNew === true;
                      const updatedRow = updated[rowIdx]?.data;
                      if (!updatedRow) return;

                      // ── Standalone: always use replaceNotesTable (debounced) ──────────
                      // This covers new rows (temp_ id typing), existing rows, and
                      // any field change — one unified save for the whole table.
                      if (isStandalone) {
                        if (field === "name") {
                          // Only call rename API when leaving cell (onBlur), not when taking a gap while typing
                          return;
                        }
                        const key = `sync-${sectionId}-${tableId}`;
                        if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]);
                        debounceRef.current[key] = setTimeout(() => {
                          onRenameSaveStart?.();
                          syncStandaloneTable(updated)
                            .catch((err) => console.error("Standalone Cashflow Workings update failed", err))
                            .finally(() => onRenameSaveEnd?.());
                        }, 600);
                        return;
                      }

                      if (String(currentRow?.data?._id || "").startsWith("temp_")) {
                        return;
                      }

                      // ── Template / library: per-field API calls ──────────
                      if (isNewRow && !String(currentRow?.data?._id || "").startsWith("temp_")) {
                        if (field === "name") {
                          const params = {
                            groupingTemplateID,
                            cashflowWorkingsID: sectionId,
                            subNoteID: tableId,
                            rowID: currentRow.data._id,
                          };
                          const payload = {
                            name: updatedRow.name || "",
                              data: sanitizeSubNoteData(
                                (Array.isArray(currentRow.data.raw?.data) && currentRow.data.raw.data.length > 0)
                                  ? currentRow.data.raw.data
                                  : createDefaultSubNoteData(updatedRow.name, updatedRow.adj, updatedRow.item)
                              ),
                          };
                            const key = `new-name-${sectionId}-${tableId}-${currentRow.data._id}`;
                            if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]);
                            debounceRef.current[key] = setTimeout(() => {
                              onRenameSaveStart?.();
                              updateSubCashflowWorkingsApiHandler(params, payload)
                                .then(() => console.log("New row name updated"))
                                .catch((err) => console.error("New row name update failed", err))
                                .finally(() => onRenameSaveEnd?.());
                            }, 500);
                        } else if (!isTemplate && (field === "cy" || field === "py" || field === "remarks")) {
                           const params = {
                             projectID: groupingTemplateID,
                             category: "cashflowWorkings",
                             groupID: sectionId,
                             subgroupID: tableId,
                             rowID: currentRow.data._id,
                           };
                           const payload = field === "remarks" ? { remarks: val } : {
                             currentYear: toNumberOrNull(updatedRow.cy),
                             previousYear: toNumberOrNull(updatedRow.py),
                           };
                           const key = `new-val-${sectionId}-${tableId}-${currentRow.data._id}-${field}`;
                           if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]);
                           debounceRef.current[key] = setTimeout(() => {
                             updatePopulateFSApiHandler(params, payload);
                           }, 500);
                        } else if (!isTemplate && (field === "adj" || field === "item")) {
                           const params = {
                             projectID: groupingTemplateID,
                             category: "cashflowWorkings",
                             groupID: sectionId,
                             subgroupID: tableId,
                             rowID: currentRow.data._id,
                           };
                           const payload = {
                            currentYear: toNumberOrNull(updatedRow.cy),
                            previousYear: toNumberOrNull(updatedRow.py),
                            cashflowSection: updatedRow.adj || "",
                            cashflowRow: updatedRow.item
                              ? {
                                  value: updatedRow.item,
                                  original: updatedRow.itemOriginal || updatedRow.item,
                                }
                              : null,
                          };
                           const key = `new-dd-${sectionId}-${tableId}-${currentRow.data._id}`;
                           if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]);
                           debounceRef.current[key] = setTimeout(() => {
                           if (!isConsol) {
                             updatePopulateFSApiHandler(params, payload)
                               .then(() => console.log("Standalone new dropdown mapping updated"))
                               .catch((err) => console.error("Standalone new dropdown mapping update failed", err));
                           }
                           }, 500);
                        }
                        return;
                      }

                      if (field === "name" || field === "cy" || field === "py" || field === "remarks" || field === "adj" || field === "item") {
                        if (isTemplate) {
                          if (field === "name" && row.data.isSubRow) {
                            const params = {
                              groupingTemplateID,
                              cashflowWorkingsID: sectionId,
                              subNoteID: row.data?._id,
                            };
                            const payload = {
                              name: updatedRow.name || "",
                              data: sanitizeSubNoteData(
                                (Array.isArray(row.data?.raw?.data) && row.data.raw.data.length > 0)
                                  ? row.data.raw.data
                                  : createDefaultSubNoteData(updatedRow.name, updatedRow.adj, updatedRow.item)
                              ),
                            };
                            const key = `sub-name-${sectionId}-${tableId}-${row.data?._id}`;
                            if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]);
                            debounceRef.current[key] = setTimeout(() => {
                              onRenameSaveStart?.();
                              updateSubCashflowWorkingsApiHandler(params, payload)
                                .then(() => console.log("Sub-note name updated"))
                                .catch((err) => console.error("Sub-note update failed", err))
                                .finally(() => onRenameSaveEnd?.());
                            }, 500);
                          } else if (field === "name" && !row.data.isSubRow && section) {
                            const newSectionData = (section.data || []).map(tbl => {
                              if (tbl._id === tableId) {
                                return buildCleanTemplateTable(
                                  tbl,
                                  row.data?._id,
                                  { name: updatedRow.name || "" },
                                  undefined,
                                  undefined,
                                  row.data?.raw?.name || row.data?.raw?.particular,
                                  buildRowMappingsFromRows(updated)
                                );
                              }
                              return sanitizeTemplateTableFormulaFields(tbl);
                            });

                            const params = {
                              groupingTemplateID,
                              cashflowWorkingsID: sectionId,
                            };
                            const payload = {
                              name: section.name || "",
                              groupingID: section.groupingID || groupingTemplateID,
                              data: newSectionData
                            };
                            
                            const key = `static-name-${sectionId}-${tableId}-${row.data?._id}`;
                            if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]);
                            debounceRef.current[key] = setTimeout(() => {
                              if (typeof updateCashflowWorkings !== 'undefined' && !isConsol) {
                                onRenameSaveStart?.();
                                updateCashflowWorkings(params, payload)
                                  .then(() => {
                                    console.log("Cashflow workings standard row name updated");
                                    if (typeof onRefresh === "function") onRefresh();
                                  })
                                  .catch((err) => console.error("Standard row name update failed", err))
                                  .finally(() => onRenameSaveEnd?.());
                              }
                            }, 500);
                          }

                          if (field === "adj" || field === "item") {
                            // Sync all mappings for this table
                            const rowMappings = updated
                              .filter(r => r.data?.name && r.data?.adj && r.data?.item)
                              .map(r => ({
                                workingsRow: r.data.name,
                                cashflowSection: r.data.adj || "",
                                cashflowRow: r.data.itemOriginal || r.data.item || null
                              }));

                            const params = {
                              groupingTemplateID,
                              cashflowWorkingsID: sectionId,
                              dataID: tableId
                            };
                            const payload = { rowMappings };
                            
                            const key = `mapping-${sectionId}-${tableId}`;
                            if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]);
                            debounceRef.current[key] = setTimeout(() => {
                              setCashflowWorkingsRowMappings(params, payload)
                                .then(() => {
                                  if (typeof onRefresh === "function") onRefresh();
                                })
                                .catch(err => console.error("Row mappings update failed", err));
                            }, 500);
                          }
                        } else if (field === "cy" || field === "py" || field === "remarks") {
                             const params = {
                               projectID: groupingTemplateID,
                               category: "cashflowWorkings",
                               groupID: sectionId,
                               subgroupID: tableId,
                               rowID: row.data?._id,
                             };
                             const payload = field === "remarks" ? { remarks: val } : {
                               currentYear: toNumberOrNull(updatedRow.cy),
                               previousYear: toNumberOrNull(updatedRow.py),
                             };
                             const key = `sub-val-${sectionId}-${tableId}-${row.data?._id}-${field}`;
                             if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]);
                             debounceRef.current[key] = setTimeout(() => {
                             if (!isConsol) {
                               updatePopulateFSApiHandler(params, payload);
                             }
                             }, 500);
                        } else if (field === "adj" || field === "item") {
                             const params = {
                               projectID: groupingTemplateID,
                               category: "cashflowWorkings",
                               groupID: sectionId,
                               subgroupID: tableId,
                               rowID: row.data?._id,
                             };
                             const payload = {
                               currentYear: toNumberOrNull(updatedRow.cy),
                               previousYear: toNumberOrNull(updatedRow.py),
                               cashflowSection: updatedRow.adj || "",
                               cashflowRow: updatedRow.item
                                 ? {
                                     value: updatedRow.item,
                                     original: updatedRow.itemOriginal || updatedRow.item,
                                   }
                                 : null,
                             };
                             const key = `sub-dd-${sectionId}-${tableId}-${row.data?._id}`;
                             if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]);
                             debounceRef.current[key] = setTimeout(() => {
                               if (!isConsol) {
                                 updatePopulateFSApiHandler(params, payload)
                                   .then(() => console.log("Standalone dropdown mapping updated"))
                                   .catch((err) => console.error("Standalone dropdown mapping update failed", err));
                               }
                             }, 500);
                        }
                      }
                    }}
                    rowData={row.data}
                    linkedCells={linkedCells}
                    rowOptions={rowOptions}
                    tableId={tableId}
                    sectionId={sectionId}
                    rowIdx={rowIdx}
                    onRefresh={onRefresh}
                    isTight={isTight}
                    isLinked={linkedCells && linkedCells[cellKey]}
                    onLinkClick={() => openPanel(cellKey, row.data?.name, row.data?._id, field)}
                  />
                </td>
              );
            })}
            <td>
             {isEditable && !isStaticRow && (
               <span style={{ 
                 display: "flex", 
                 alignItems: "center", 
                 gap: "8px",
                 opacity: hoveredRowIdx === rowIdx ? 1 : 0,
                 transition: "opacity 0.2s",
                 pointerEvents: hoveredRowIdx === rowIdx ? "auto" : "none"
               }}>
                 <span
                   onClick={() => {
                     const updated = [...safeRows];
                     const isCurrentlyBold = updated[rowIdx].data?.isBold;
                     updated[rowIdx] = {
                       ...updated[rowIdx],
                       data: {
                         ...updated[rowIdx].data,
                         isBold: !isCurrentlyBold
                       }
                     };
                     setRows(updated);
                     if (isStandalone) {
                       syncStandaloneTable(updated);
                     } else if (isTemplate) {
                       // Trigger API update for non-standalone template row
                       const isSubRow = row.data?.isSubRow === true;
                       if (isSubRow) {
                         const params = {
                           groupingTemplateID,
                           cashflowWorkingsID: sectionId,
                           subNoteID: row.data?._id,
                         };
                         const payload = {
                           isBold: !isCurrentlyBold
                         };
                         const key = `sub-bold-${sectionId}-${tableId}-${row.data?._id}`;
                         if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]);
                         debounceRef.current[key] = setTimeout(() => {
                           if (typeof updateSubCashflowWorkingsApiHandler !== 'undefined' && !isConsol) {
                             updateSubCashflowWorkingsApiHandler(params, payload)
                               .then(() => {
                                 console.log("Standalone sub-note bold updated");
                                 if (typeof onRefresh === 'function') onRefresh();
                               })
                               .catch((err) => console.error("Standalone sub-note bold update failed", err));
                           }
                         }, 500);
                       } else if (section) {
                          // Full template body update for static rows
                          const newSectionData = (section.data || []).map(tbl => {
                            if (tbl._id === tableId) {
                              return buildCleanTemplateTable(
                                tbl, row.data?._id, { isBold: !isCurrentlyBold },
                                undefined, undefined, undefined,
                                buildRowMappingsFromRows(updated)
                              );
                            }
                            return sanitizeTemplateTableFormulaFields(tbl);
                          });

                          const params = {
                            groupingTemplateID,
                            cashflowWorkingsID: sectionId,
                          };
                          const payload = {
                            name: section.name || "",
                            groupingID: section.groupingID || groupingTemplateID,
                            data: newSectionData
                          };
                          
                          const key = `static-bold-${sectionId}-${tableId}-${row.data?._id}`;
                          if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]);
                          debounceRef.current[key] = setTimeout(() => {
                            if (typeof updateCashflowWorkings !== 'undefined' && !isConsol) {
                              updateCashflowWorkings(params, payload)
                                .then(() => {
                                  console.log("Cashflow workings template bold updated");
                                  if (typeof onRefresh === 'function') onRefresh();
                                })
                                .catch((err) => console.error("Cashflow workings template bold update failed", err));
                            }
                          }, 500);
                        }
                     }
                   }}
                   style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                   title="Toggle Bold"
                 >
                   <img 
                     src={BoldIcon} 
                     alt="bold" 
                     style={{ 
                       width: 14, 
                       height: 14, 
                       display: "block",
                       opacity: row.data?.isBold ? 1 : 0.4 
                     }} 
                   />
                 </span>
                 <span
                  className={tableStyles.deleteIcon}
                  onClick={() => {
                    const rowId = row.data?._id;
                    const isBackendRow = rowId && !String(rowId).startsWith("temp_");
                    const isSubRow = row.data?.isSubRow === true;

                    if (isBackendRow) {
                      setRowToDelete({ rowId, rowIdx, isSubRow, name: row.data?.name });
                    } else {
                      const updated = safeRows.filter((_, i) => i !== rowIdx);

                      setRows(updated);

                      if (isStandalone) {
                        syncStandaloneTable(updated, true)
                          .then(() => {
                            if (typeof onRefresh === "function") onRefresh();
                          })
                          .catch((err) => console.error("Standalone delete sync failed", err));
                      }
                    }
                  }}
                >
                  <DeleteRedIcon style={{ width: "15px", height: "15px" }} />
                </span>
              </span>
             )}
            </td>
          </tr>
        );
      })}
      {isEditable && (
        <tr>
          <td colSpan={FIELDS.length + 2}>
            <button
              onClick={addNewRow}
              style={{
                background: "none",
                border: "none",
                color: "#2D8861",
                cursor: "pointer",
                fontWeight: "500",
                padding: "8px 0",
                fontSize: "15px",
              }}
            >
              <Add /> New Line Item
            </button>
          </td>
        </tr>
      )}
      {rowToDelete !== null && (
        <DeletePopup
          isOpen={true}
          title="Are you sure?"
          message="Once deleted, you will not be able to recover this line item!"
          onConfirm={handleConfirmDelete}
          onCancel={() => setRowToDelete(null)}
        />
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ErrorBoundary
// ─────────────────────────────────────────────────────────────────────────────

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error("CashflowWorkings crashed:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24, color: "red" }}>
          <b>Something went wrong loading Cashflow Workings.</b>
          <pre style={{ fontSize: 12 }}>{this.state.error?.message}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// CashflowWorkingsInner — main component
// ─────────────────────────────────────────────────────────────────────────────

const CashflowWorkingsInner = ({
  hideLinkIcon = false,
  groupingTemplateID,
  cashflowWorkingsID,
  dataID,
  setHandleSave,
  handleSave,
  autoSave,
  templateCashflowWorkings,
  isTemplateProp = false,
  isDisclosureProp = false,
  isReadOnly = false, 
  isStandalone = false,
  isConsol = false,
}) => {
  // const isTemplate = isTemplateProp;
  const isTemplate = isTemplateProp && !dataID;
  const isDefaultTemplate = isTemplateProp && dataID;
  const [showPanel, setShowPanel]         = useState(false);
  const [activeCellKey, setActiveCellKey] = useState(null);
  const [rowOptions, setRowOptions]       = useState([]);
  const [linkedCells, setLinkedCells]     = useState({});
  const [selectedCell, setSelectedCell]   = useState(null);
  const [tableRowsMap, setTableRowsMap]   = useState({});
  const [apiData, setApiData]             = useState([]);
  const [isStandaloneDataLoading, setIsStandaloneDataLoading] = useState(
    !isTemplate && !isConsol
  );
  const [templateSections, setTemplateSections] = useState([]);
  const [currentYearHeader, setCurrentYearHeader] = useState("Current Year");
  const [previousYearHeader, setPreviousYearHeader] = useState("Previous Year");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const isDisclosureTable = isDisclosureProp;
  
  const isEditable = !isReadOnly && !isDefaultTemplate;
  const tableRowsMapRef = useRef(tableRowsMap);
  const modifiedTableIdsRef = useRef(new Set());
  useEffect(() => {
    tableRowsMapRef.current = tableRowsMap;
  }, [tableRowsMap]);

  // ── Transform helpers ─────────────────────────────────────────────────────

  const transformTemplateToRows = (tableRows = [], rowMappings = [], subNotes = []) => {
    return [
      { type: "opening" },
      ...(Array.isArray(tableRows) ? tableRows : []).map((row) => {
        const mapping = findRowMapping(rowMappings, row?.name || row?.particular);
        return {
          type: "data",
          data: {
            _id:      row._id,
            name:     mapping?.workingsRow || row.name || row.particular || "",
            cy: row?.currentYear ?? "",
            py: row?.previousYear ?? "",
            adj:      mapping?.cashflowSection || "",
            item:     mapping?.cashflowRow     || "",
            itemOriginal: mapping?.cashflowRow || "",
            amountCY: row?.reflectedCY ?? "",
            amountPY: row?.reflectedPY ?? "",
            remarks:  row?.remarks || "",
            // Standard template rows are not sub-notes, so we don't set isSubRow: true here
            raw:      row,
            isBold:   row.isBold || false,
          },
        };
      }),
      ...(Array.isArray(subNotes) ? subNotes : []).map((row) => {
        const mapping = findRowMapping(rowMappings, row?.name || row?.particular);
        return {
          type: "data",
          data: {
            _id:      row._id,
            name:     mapping?.workingsRow || row.name || row.particular || "",
            cy:       row?.currentYear ?? "",
            py:       row?.previousYear ?? "",
            adj:      mapping?.cashflowSection || row?.cashflowSection || "",
            item:     mapping?.cashflowRow     || row?.cashflowRow     || "",
            itemOriginal: mapping?.cashflowRow || row?.cashflowRow     || "",
            amountCY: row?.reflectedCY ?? "",
            amountPY: row?.reflectedPY ?? "",
            remarks:      row?.remarks || "",
            isSubRow:     true,
            raw:          row,
            isBold:       row.isBold || false,
          },
        };
      }),
      { type: "closing" },
    ];
  };

const transformSubgroupToRows = (
  table = [],
  rowMappings = [],
  cellFormulas = [],
  subgroupId = ""
) => {
  const tableRows = Array.isArray(table) ? table : [];
  const mappings = Array.isArray(rowMappings) ? rowMappings : [];

  return [
    { type: "opening" },       
    ...tableRows.map((row) => {
      const rowName = row.particular || row.name || "";
      const mapping = findRowMapping(mappings, rowName);
      return {
        type: "data",             
        data: {
          _id:          row._id,
          name:         rowName,
          cy:           row.currentYear     ?? "",
          py:           row.previousYear    ?? "",
          adj:          mapping?.cashflowSection || row.cashflowSection || "",
          item:         mapping?.cashflowRow || mapping?.cashflowRowID || (typeof row.cashflowRow === "object" && row.cashflowRow !== null
                          ? (row.cashflowRow._id || row.cashflowRow.value || row.cashflowRow.original || "")
                          : (row.cashflowRow || "")),
          itemOriginal: mapping?.cashflowRow || mapping?.cashflowRowID || (typeof row.cashflowRow === "object" && row.cashflowRow !== null
                          ? (row.cashflowRow.original || row.cashflowRow.value || "")
                          : (row.cashflowRow || "")),
          amountCY:     row.reflectedCY ?? "",
          amountPY:     row.reflectedPY ?? "",
          remarks:      row.remarks || "",
          isSubRow:     true,
          raw:          row,
          isBold:       row.isBold || false,
        },
      };
    }),
    { type: "closing" },        // static bottom spacer → triggers "New Line Item" button
  ];
};

  // ── Save handler — createSubCashflowWorkings called HERE for new rows ─────

  const handleSaveMappings = useCallback(async () => {
    if (!isEditable || isConsol) return;
    try {
      swal({
        title: "Saving...",
        text: "Please wait",
        buttons: false,
        closeOnClickOutside: false,
      });

      const modifiedIds = modifiedTableIdsRef.current;

      for (const section of templateSections) {
        for (const table of section.data || []) {
          const rows = tableRowsMap[table._id] || [];

          // ── 1. Create new rows via createSubCashflowWorkings ──────────────
          // Collect rows that are still temp (isNew + temp_ id = never created)
          const newUnsavedRows = rows.filter(
            (r) =>
              r.type === "data" &&
              r.data.isNew === true &&
              String(r.data._id || "").startsWith("temp_")
          );

          // Skip tables that were not modified and have no unsaved new rows
          if (!modifiedIds.has(table._id) && newUnsavedRows.length === 0) {
            continue;
          }

          if (newUnsavedRows.length > 0) {
            try {
              const dataRows = rows.filter((r) => r.type === "data" && r.data?.name);
              const tableRowsPayload = dataRows.map((r) => ({
                name: r.data.name,
                lineType: r.data.raw?.lineType || "line item",
                isBold: r.data.isBold || false,
                ...(r.data.raw?.formula ? { formula: r.data.raw.formula } : {}),
                ...(r.data.raw?.formulaType ? { formulaType: r.data.raw.formulaType } : {}),
              }));

              const newSectionData = (section.data || []).map((tbl) => {
                if (tbl._id !== table._id) return sanitizeTemplateTableFormulaFields(tbl);
                const cleanTbl = buildCleanTemplateTable(
                  { ...tbl, tableRow: tableRowsPayload },
                  undefined, undefined, undefined, undefined, undefined,
                  buildRowMappingsFromRows(rows)
                );
                delete cleanTbl.subNotes;
                return cleanTbl;
              });

              await updateCashflowWorkings(
                {
                  groupingTemplateID,
                  cashflowWorkingsID: section._id,
                },
                {
                  name: section.name || "",
                  groupingID: section.groupingID || groupingTemplateID,
                  data: newSectionData,
                }
              );
            } catch (err) {
              console.error("updateCashflowWorkings failed for new rows:", err);
            }
          }


        }
      }

      modifiedTableIdsRef.current.clear();

      swal.close();
      setSuccessMessage("Row saved successfully");
      setShowSuccessPopup(true);
      fetchTemplateData();

    } catch (err) {
      console.error("Save error", err);
      swal.close();
      swal({
        title: "Error",
        text: err?.response?.data?.error || "Something went wrong",
        icon: "error",
        button: "OK",
      });
    }
  }, [templateSections, groupingTemplateID, tableRowsMap]);

  useEffect(() => {
    if (setHandleSave) {
      setHandleSave(handleSaveMappings);
    }
  }, [handleSaveMappings]);

  // ── Panel helpers ─────────────────────────────────────────────────────────

  const openPanel = (cellKey, row, rowId, col, sectionId, tableId) => {
    setActiveCellKey(cellKey);
    setSelectedCell({ row, rowId, col, sectionId, tableId });
    setShowPanel(true);
  };

  const handleApply = (data, updatedTemplateOrProject) => {
    setLinkedCells((prev) => {
      const updated = { ...prev };
      if (!data) {
        delete updated[activeCellKey];
      } else {
        updated[activeCellKey] = data;
      }
      return updated;
    });

    if (updatedTemplateOrProject) {
      if (updatedTemplateOrProject.cashflowWorkings) {
        fetchTemplateData(updatedTemplateOrProject);
      } else if (updatedTemplateOrProject.data) {
        fetchStandaloneData(updatedTemplateOrProject);
      }
    }
  };

  // ── TEMPLATE fetch ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!templateSections?.length || !Object.keys(tableRowsMap).length) return;

    const linkedMap = {};

    templateSections.forEach((section) => {
      (section.data || []).forEach((table) => {
        const formulas = Array.isArray(table.cellFormulas) ? table.cellFormulas : [];

        formulas.forEach((f) => {
          const rows = tableRowsMap[table._id] || [];

          rows.forEach((row, rowIdx) => {
            if (row.type !== "data") return;

            if (normalize(row.data.name) === normalize(f.row)) {
              const colIndexMap = {
                cy: 1,
                py: 2,
                currentYear: 1,
                previousYear: 2,
              };

              const colIdx = colIndexMap[f.col];
              if (colIdx !== undefined) {
                const cellKey = `${table._id}-${row.data?._id || rowIdx}-${colIdx}`;
                linkedMap[cellKey] = f;
              }
            }
          });
        });
      });
    });

    setLinkedCells(linkedMap);
  }, [templateSections, tableRowsMap]);

  useEffect(() => {
    if (isTemplate || !apiData?.length || !Object.keys(tableRowsMap).length) return;

    const linkedMap = {};

    apiData.forEach((section) => {
      (section.subgroup || []).forEach((table) => {
        const isCustom = table?.isCustom === true;
        const isText = table?.dataType === "text" || table?.datatype === "text";
        if (isCustom || isText) return;

        const formulas = Array.isArray(table.cellFormulas) ? table.cellFormulas : [];

        formulas.forEach((f) => {
          const rows = tableRowsMap[table._id] || [];

          rows.forEach((row, rowIdx) => {
            if (row.type !== "data") return;

            if (
              normalize(row.data.name) === normalize(f.row) ||
              cleanAlphaNum(row.data.name) === cleanAlphaNum(f.row) ||
              (cleanAlphaNum(row.data.name).length >= 4 &&
                (cleanAlphaNum(row.data.name).startsWith(cleanAlphaNum(f.row)) ||
                 cleanAlphaNum(f.row).startsWith(cleanAlphaNum(row.data.name))))
            ) {
              const colIndexMap = {
                cy: 1,
                py: 2,
                currentYear: 1,
                previousYear: 2,
                amountCY: 1,
                amountPY: 2,
              };

              const colIdx = colIndexMap[f.col];
              if (colIdx !== undefined) {
                const cellKey = `${table._id}-${row.data?._id || rowIdx}-${colIdx}`;
                linkedMap[cellKey] = f;
              }
            }
          });
        });
      });
    });

    setLinkedCells(linkedMap);
  }, [apiData, tableRowsMap, isTemplate]);

  const fetchTemplateData = useCallback((updatedTemplate) => {
    if (updatedTemplate && updatedTemplate.cashflowWorkings) {
      const cashflowWorkings = updatedTemplate.cashflowWorkings;
      if (Array.isArray(cashflowWorkings) && cashflowWorkings.length) {
        const map = {};
        cashflowWorkings.forEach((section) => {
          (section.data || []).forEach((table) => {
            const combinedSubNotes = [
              ...(Array.isArray(table.subNotes) ? table.subNotes : []),
              ...(Array.isArray(section.subNotes) ? section.subNotes : [])
            ];
            map[table._id] = transformTemplateToRows(
              table.tableRow,
              table.rowMappings,
              combinedSubNotes
            );
          });
        });
        setTableRowsMap(map);
        setTemplateSections(cashflowWorkings);
        setLinkedCells({});
        modifiedTableIdsRef.current.clear();
      }
      return;
    }
    if (!groupingTemplateID || isConsol) return;

    getTemplateDetailsApiHandler({ groupingTemplateID })
      .then((res) => {
        if (res?.data?.success) {
          const cashflowWorkings = res.data.groupingTemplate?.cashflowWorkings;
          if (Array.isArray(cashflowWorkings) && cashflowWorkings.length) {
            const map = {};
            cashflowWorkings.forEach((section) => {
              (section.data || []).forEach((table) => {
                // Merge table-level and section-level subNotes (handling backend inconsistency)
                const combinedSubNotes = [
                  ...(Array.isArray(table.subNotes) ? table.subNotes : []),
                  ...(Array.isArray(section.subNotes) ? section.subNotes : [])
                ];
                
                map[table._id] = transformTemplateToRows(
                  table.tableRow,
                  table.rowMappings,
                  combinedSubNotes
                );
              });
            });
            setTableRowsMap(map);
            setTemplateSections(cashflowWorkings);
            setLinkedCells({});
            modifiedTableIdsRef.current.clear();
          }
        }
      })
      .catch((err) => {
        console.error("Template cashflow workings API ERROR:", err);
      });
  }, [groupingTemplateID]);

  useEffect(() => {
    if (isTemplate === false || !groupingTemplateID) return;
    fetchTemplateData();
  }, [groupingTemplateID, isTemplate]);

  // ── STANDALONE fetch ──────────────────────────────────────────────────────

  const fetchStandaloneData = useCallback((updatedProject) => {
    if (updatedProject && updatedProject.data) {
      setApiData(Array.isArray(updatedProject.data) ? updatedProject.data : []);
      if (updatedProject.headers) {
        setCurrentYearHeader(updatedProject.headers.currentYear || "Current Year");
        setPreviousYearHeader(updatedProject.headers.previousYear || "Previous Year");
      } else {
        setCurrentYearHeader(updatedProject.currentYear || "Current Year");
        setPreviousYearHeader(updatedProject.previousYear || "Previous Year");
      }
      setIsStandaloneDataLoading(false);
      return;
    }
    if (isTemplate || !groupingTemplateID) {
      setIsStandaloneDataLoading(false);
      return;
    }

    setIsStandaloneDataLoading(true);

    const apiCall = isConsol
      ? getConsolPopulateFSDetailsApiHandler({
          consolID: groupingTemplateID,
          category: "cashflowWorkings",
        })
      : getPopulateFSDetailsApiHandler({
          projectID: groupingTemplateID,
          category: "cashflowWorkings",
        });

    apiCall
      .then((res) => {
        if (res?.data?.success) {
          const data = res.data.data;
          setApiData(Array.isArray(data) ? data : []);
          if (res.data.headers) {
            setCurrentYearHeader(res.data.headers.currentYear || "Current Year");
            setPreviousYearHeader(res.data.headers.previousYear || "Previous Year");
          } else {
            setCurrentYearHeader(res.data.currentYear || "Current Year");
            setPreviousYearHeader(res.data.previousYear || "Previous Year");
          }
        } else {
          console.warn("Standalone API returned success = false", res?.data);
          setApiData([]);
        }
      })
      .catch((err) => {
        console.error("Standalone API ERROR:", err);
        setApiData([]);
      })
      .finally(() => {
        setIsStandaloneDataLoading(false);
      });
  }, [groupingTemplateID, isTemplate, isConsol]);

  useEffect(() => {
    fetchStandaloneData();
  }, [fetchStandaloneData]);

  // ── Build tableRowsMap from standalone apiData ────────────────────────────

  useEffect(() => {
    if (!apiData.length) return;
    const map = {};
    apiData.forEach((section) => {
      (section.subgroup || []).forEach((sg) => {
        const isCustom = sg?.isCustom === true;
        const isText = sg?.dataType === "text" || sg?.datatype === "text";
        if (sg?._id && !isCustom && !isText) {
          map[sg._id] = transformSubgroupToRows(
            sg.table || [],
            sg.rowMappings || [],
            sg.cellFormulas || [],
            sg._id
          );
        }
      });
    });
    setTableRowsMap(map);
  }, [apiData]);

  // ── Row options (sections + cashflow rows for dropdowns) ──────────────────

  useEffect(() => {
    if (!groupingTemplateID) return;

    if (isTemplate && !isConsol) {
      getCashflowRowOptions(groupingTemplateID)
        .then((res) => {
          if (res?.data?.success) {
            setRowOptions(
              Array.isArray(res.data.options) ? res.data.options : []
            );
          }
        })
        .catch((err) => console.error("getCashflowRowOptions error:", err));
    } else {
      const apiCall = isConsol
        ? getConsolCashflowDropdowns(groupingTemplateID)
        : getCashflowDropdowns(groupingTemplateID);

      apiCall
        .then((res) => {
          if (res?.data?.success) {
            const opts = Array.isArray(res.data.options)
              ? res.data.options
              : [];
            setRowOptions(opts);
          }
        })
        .catch((err) => console.error("getCashflowDropdowns error:", err));
    }
  }, [groupingTemplateID, isTemplate]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-3" style={isStandalone ? { padding: "0px" } : {}}>
      <div
        className={`${tableStyles.tableContainer} ${(isTemplate || isStandalone) ? tableStyles.hideScrollbar : ""}`}
        style={{
          position: "relative",
          ...(!isTemplate
            ? {
                height: "calc(100vh - 80px)",
                maxHeight: "calc(100vh - 80px)",
                overflow: "auto",
                width: "100%",
              }
            : {})
        }}
      >
        {isStandalone && isStandaloneDataLoading && (
          <div
            role="status"
            aria-label="Loading Cashflow Workings"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255, 255, 255, 0.82)",
            }}
          >
            <Spinner animation="border" role="presentation" style={{ color: "#205a46" }} />
          </div>
        )}
        <table className={tableStyles.table}>
          <thead>
            <tr>
              <th>Adjustment Name</th>
              <th style={{ textAlign: "right", paddingRight: "38px" }}>{currentYearHeader}</th>
              <th style={{ textAlign: "right", paddingRight: "38px" }}>{previousYearHeader}</th>
              <th>Adjustment in Cashflow Statement</th>
              <th>Name of the Item in Cashflow Statement</th>
              <th>Amount to be reflected in cashflow (cy)</th>
              <th>Amount to be reflected in cashflow (py)</th>
              <th>Remarks</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {isTemplate
              ? templateSections.map((section) => (
                  <React.Fragment key={section._id}>
                    {(section.data || []).map((table) => (
                      <React.Fragment key={table._id}>
                        <tr className={tableStyles.titleRow}>
                          <td colSpan={9}>{table.tableTitle}</td>
                        </tr>
                        <TableSection
                          setSuccessMessage={setSuccessMessage}
                          setShowSuccessPopup={setShowSuccessPopup}
                          title=""
                          section={section}
                          rows={
                            Array.isArray(tableRowsMap[table._id])
                              ? tableRowsMap[table._id]
                              : []
                          }
                          setRows={(updatedRows) => {
                            modifiedTableIdsRef.current.add(table._id);
                            setTableRowsMap((prev) => ({
                              ...prev,
                              [table._id]: updatedRows,
                            }));
                          }}
                          isEditable={isEditable}
                          openPanel={(cellKey, row, rowId, col) =>
                            openPanel(cellKey, row, rowId, col, section._id, table._id)
                          }
                          linkedCells={linkedCells}
                          hideLinkIcon={hideLinkIcon}
                          rowOptions={rowOptions}
                          tableId={table._id}
                          sectionId={section._id}
                          isTemplate={isTemplate}
                          // isEditable={isEditable}  
                          isDisclosureTable={isDisclosureTable}
                          groupingTemplateID={groupingTemplateID}
                          isStandalone={isStandalone}
                          isConsol={isConsol}
                          onRefresh={fetchTemplateData}
                        />
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))
              : apiData.map((section) => {
                  if (!section?._id) return null;
                  return (
                    <React.Fragment key={section._id}>
                      {(section.subgroup || []).map((sg) => {
                        const isCustom = sg?.isCustom === true;
                        const isText = sg?.dataType === "text" || sg?.datatype === "text";
                        if (!sg?._id || isCustom || isText) return null;
                        return (
                          <React.Fragment key={sg._id}>
                            <tr className={tableStyles.titleRow}>
                              <td colSpan={9}>{sg.tableTitle || ""}</td>
                            </tr>
                            <TableSection
                              setSuccessMessage={setSuccessMessage}
                              setShowSuccessPopup={setShowSuccessPopup}
                              title=""
                              section={section}
                              tableId={sg._id}
                              sectionId={section._id}
                              isTemplate={isTemplate}
                              isStandalone={isStandalone}
                              isEditable={isEditable}
                              rows={
                                Array.isArray(tableRowsMap[sg._id])
                                  ? tableRowsMap[sg._id]
                                  : []
                              }
                              setRows={(updatedRows) =>
                                setTableRowsMap((prev) => ({
                                  ...prev,
                                  [sg._id]: updatedRows,
                                }))
                              }
                              groupingTemplateID={groupingTemplateID}
                              openPanel={(cellKey, row, rowId, col) =>
                                openPanel(cellKey, row, rowId, col, section._id, sg._id)
                              }
                              linkedCells={linkedCells}
                              hideLinkIcon={hideLinkIcon}
                              rowOptions={rowOptions}
                              isConsol={isConsol}
                              onRefresh={fetchStandaloneData}
                            />
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
          </tbody>
        </table>
      </div>

      <SidePanel
        type="cashflow"
        activeCellKey={activeCellKey}
        isTemplate={isTemplate}
        showLinkPanel={showPanel}
        setShowLinkPanel={setShowPanel}
        onApply={handleApply}
        groupingTemplateID={groupingTemplateID}
        selectedCell={selectedCell}
        cashflowWorkingsID={selectedCell?.sectionId}
        dataID={selectedCell?.tableId}
        table={isTemplate
          ? templateSections
              ?.find((s) => s._id === selectedCell?.sectionId)
              ?.data?.find((t) => t._id === selectedCell?.tableId)
          : apiData
              ?.find((s) => s._id === selectedCell?.sectionId)
              ?.subgroup?.find((t) => t._id === selectedCell?.tableId)
        }
        onSaveSuccess={isTemplate ? fetchTemplateData : fetchStandaloneData}
        isConsol={isConsol}
        consolID={groupingTemplateID}
      />
      <SuccessPopup
        isOpen={showSuccessPopup}
        message={successMessage}
        onClose={() => {
          setShowSuccessPopup(false);
          if (isStandalone && typeof fetchStandaloneData === "function") {
            fetchStandaloneData();
          }
        }}
      />
    </div>
  );
};


const CashflowWorkings = (props) => (
  <ErrorBoundary>
    <CashflowWorkingsInner {...props} />
  </ErrorBoundary>
);

export default CashflowWorkings;